import type { QueryClient } from '@tanstack/react-query'
import { getConfiguration } from '@features/settings/api/configuration.api'
import { settingsQueryKeys } from '@features/settings/api/queryKeys'
import { hydrateResolvedConfiguration } from '@features/settings/sync/settingsResolutionSync'
import { recordsQueryKeys } from '@features/records/api/queryKeys'
import { sidebarQueryKeys } from '@features/sidebar/api/queryKeys'
import { tablesQueryKeys } from '@features/tables/api/queryKeys'
import { permissionsQueryKeys } from '../api/queryKeys'

/**
 * Re-asks what the signed-in user may do after a change that can grant or
 * take it away: a grant, a group's members, its filter, its master admin
 * switch, or deleting it. The change may be your own, so it is re-asked
 * whoever it was for: the modules in the navigation, the tables, fields and
 * rows you can see, whether Add table shows, and whether you're an
 * administrator. The permission catalog the group pages list is the same for
 * everyone and is left alone. Only open screens refetch now.
 */
export function refreshOwnCapabilities(queryClient: QueryClient) {
  // The table list, each table's fields and whether Add table shows.
  void queryClient.invalidateQueries({ queryKey: tablesQueryKeys.tables })
  void queryClient.invalidateQueries({ queryKey: recordsQueryKeys.recordsAll })
  void queryClient.invalidateQueries({ queryKey: sidebarQueryKeys.tableOrganization })
  void queryClient.invalidateQueries({
    queryKey: permissionsQueryKeys.permissionsModules,
    exact: true,
  })
  void queryClient.invalidateQueries({ queryKey: ['permissions', 'resolved'] })
  void refreshAdministrator(queryClient)
}

// Whether you're an administrator comes with the configuration.
async function refreshAdministrator(queryClient: QueryClient) {
  try {
    const configuration = await queryClient.fetchQuery({
      queryKey: settingsQueryKeys.configuration,
      queryFn: ({ signal }) => getConfiguration(signal),
      staleTime: 0,
    })
    hydrateResolvedConfiguration(configuration)
  } catch {
    // The save went through; the next configuration read picks it up.
  }
}
